import React, { useEffect, useState, useRef } from 'react';
import { Button, Form, InputGroup, FormControl, Card, ListGroup, Alert, Spinner } from 'react-bootstrap';
import { FaPaperPlane, FaCircle, FaCommentAlt } from 'react-icons/fa'; // Iconos para la UI
import Swal from 'sweetalert2';

const Mytrip = ({ ticket, user_id }) => {
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState('');
    const [user1Online, setUser1Online] = useState(null); // null indica que no se sabe si están en línea
    const [user2Online, setUser2Online] = useState(null);
    const [ws, setWs] = useState(null); // WebSocket connection
    const [isTyping, setIsTyping] = useState(false); // Estado de "escribiendo"
    const [welcomeMessage, setWelcomeMessage] = useState(''); // Mensaje de bienvenida al chat
    const [sending, setSending] = useState(false); // Estado de carga para el botón de envío
    const messagesEndRef = useRef(null); // Ref para el final de la lista de mensajes
    const isConnectedRef = useRef(false); // Ref para trackear el estado de conexión
    const typingTimeout = useRef(null); // Ref para manejar el tiempo de inactividad al escribir

    // Función para reconectar WebSocket
    const connectWebSocket = () => {
        if (ws && ws.readyState === WebSocket.OPEN) {
            console.log('La conexión WebSocket ya está abierta');
            ws.send(JSON.stringify({ ticket, user_id, isOnline: true }));
            return; // No hacemos nada si ya está conectado
        }
        
        Swal.fire({
            title: 'Conectando...',
            html: 'Estamos conectando con el servidor...',
            timerProgressBar: true,
            didOpen: () => {
                Swal.showLoading(); // Mostrar cargador
            },
        });

        // Crear la conexión WebSocket con los parámetros
        //const socket = new WebSocket(`ws://${window.location.hostname}:8080/?ticket=${ticket}&user_id=${user_id}`);
        const socket = new WebSocket(`wss://chat.adandyou.mx/ws/?ticket=${ticket}&user_id=${user_id}`);

        socket.onopen = () => {
            console.log('Conexión WebSocket establecida');
            isConnectedRef.current = true; // Marcamos como conectado
            setWelcomeMessage(`¡Bienvenido al chat, Usuario ${user_id}!`); // Mostrar el mensaje de bienvenida
            setMessages([]);
            Swal.close();
        };

        socket.onmessage = (event) => {
             const data = JSON.parse(event.data);
            // Verificar si el WebSocket está abierto antes de actualizar los mensajes
            if (isConnectedRef.current) {
                if (data.message) {
                    setMessages((prevMessages) => {
                        const maxMessages = 50; // Mantener solo los últimos 50 mensajes
                        const newMessages = [...prevMessages, data];
                        return newMessages.slice(-maxMessages);
                    });
                }

                // Recibir el estado de "escribiendo" del otro usuario
                if (data.isTyping !== undefined) {
                    setIsTyping(data.isTyping);
                }
                if (data.isOnline !== undefined) {
                    setUser1Online(data.isOnline);
                    setUser2Online(data.isOnline);
                }

                if (data.user1_online !== undefined) setUser1Online(data.user1_online);
                if (data.user2_online !== undefined) setUser2Online(data.user2_online);
            }
        };

        socket.onerror = (error) => {
            console.error('Error en WebSocket:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error de conexión',
                text: 'No se pudo conectar con el servidor. Intenta más tarde.',
            });
        };

        socket.onclose = () => {
            console.log('Desconectado de WebSocket');
            isConnectedRef.current = false; // Marcamos como desconectado

            // Solo actualizar los estados de los usuarios si se está reconociendo la desconexión correctamente
            setUser1Online(false);
            setUser2Online(false);

            Swal.fire({
                icon: 'warning',
                title: 'Desconectado',
                text: 'La conexión al servidor se perdió.',
                showCancelButton: true,
                confirmButtonText: 'Reconectar',
                cancelButtonText: 'Cancelar',
            }).then((result) => {
                if (result.isConfirmed) {
                    connectWebSocket(); // Intentar reconectar
                }
            });
        };

        setWs(socket);
    };

    useEffect(() => {
        if (!ticket || !user_id) {
            console.error('Los parámetros ticket o user_id no están definidos.');
            return;
        }

        connectWebSocket(); // Intentar conectar al WebSocket al montar el componente

        // Cleanup cuando el componente se desmonta
        return () => {
            if (ws) {
                ws.close();
            }
        };
    }, [ticket, user_id]); // Solo se ejecutará cuando los parámetros cambien

    useEffect(() => {
        // Desplazar al final de los mensajes cuando cambie el arreglo de mensajes
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Este efecto maneja el desplazamiento al final cuando cambia `isTyping`
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isTyping]);  // Se ejecuta cuando el estado `isTyping` cambia

    const handleSendMessage = () => {
        if (message.trim() === '') return;

        // Verificar que el WebSocket esté abierto antes de enviar
        if (ws && ws.readyState === WebSocket.OPEN) {
            setSending(true);
            const msg = {
                ticket,
                user_id,
                message,
            };
            ws.send(JSON.stringify(msg));
            setMessage('');
            setSending(false); // Resetear estado de envío después de enviar el mensaje
        } else {
            console.error('WebSocket no está abierto');
        }
    };

    const handleChangeMessage = (e) => {
        setMessage(e.target.value);
    
        // Si el mensaje no está vacío, enviar el estado de "escribiendo"
        if (e.target.value.trim() !== '') {
            // Enviar el estado de "escribiendo" al servidor
            if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ ticket, user_id, isTyping: true }));
            }
        } else {
            // Si el mensaje está vacío, enviar el estado de "no está escribiendo"
            if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ ticket, user_id, isTyping: false }));
            }
        }
    
        // Resetear el estado de "escribiendo" después de 2 segundos de inactividad
        clearTimeout(typingTimeout.current);
        typingTimeout.current = setTimeout(() => {
            // Si el campo de texto está vacío después de 2 segundos, enviar "no está escribiendo"
            if (message.trim() === '') {
                ws.send(JSON.stringify({ ticket, user_id, isTyping: false }));
            }
        }, 2000);
    };
    

    const handleBlurMessage = () => {
        // Enviar el estado de "ya no está escribiendo" al servidor
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ ticket, user_id, isTyping: false }));
        }
    };

    return (
        <div className="container mt-4" style={{ maxWidth: '600px' }}>
            <Card>
                <Card.Header as="h5" className="d-flex justify-content-between align-items-center" style={{
                    background: 'linear-gradient(135deg, #00b4d8, #0078d4)',  // Degradado de colores
                    color: 'white',
                    borderRadius: '10px 10px 0 0',
                    padding: '10px 20px'
                }}>
                    <div className="d-flex align-items-center">
                        <FaCommentAlt size={22} className="me-3" />
                        <span>Conversación {ticket}</span>
                    </div>
                    <div className="d-flex align-items-center">
                        <span className="me-2">
                            {isConnectedRef.current
                                ? (user1Online && user2Online ? 'Ambos usuarios están en línea' : 'Esperando a los usuarios')
                                : 'Esperando conexión con el servidor'}
                        </span>
                        <FaCircle color={isConnectedRef.current && user1Online && user2Online ? "green" : "gray"} size={18} />
                    </div>
                </Card.Header>
                <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <ListGroup variant="flush">
                        {messages.length === 0 ? (
                            <ListGroup.Item className="text-center">No hay mensajes en esta conversación.</ListGroup.Item>
                        ) : (
                            messages.map((msg, index) => (
                                <ListGroup.Item key={index} className={msg.sender_id === user_id ? 'text-end' : ''} style={{ padding: '8px 16px' }}>
                                    <div
                                        style={{
                                            backgroundColor:msg.sender_id === user_id ? '#0078d4' : '#e5e5e5',
                                            color: msg.sender_id === user_id ? 'white' : 'black',
                                            borderRadius: '20px',
                                            maxWidth: '70%',
                                            display: 'inline-block',
                                            padding: '10px 15px',
                                            marginBottom: '5px',
                                            boxShadow: msg.sender_id === user_id ? '0 4px 8px rgba(0, 120, 212, 0.3)' : 'none',
                                        }}
                                    >
                                        <strong>{msg.sender_id === user_id ? 'Tú' : `Usuario ${msg.sender_id}`}: </strong>
                                        {msg.message}
                                    </div>
                                </ListGroup.Item>
                            ))
                        )}
                    </ListGroup>
                    {isTyping && (
                        <div className="text-center mt-2" style={{ fontStyle: 'italic', color: '#0078d4' }}>
                            El otro usuario está escribiendo...
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </Card.Body>
                <Card.Footer className="d-flex align-items-center">
                    <InputGroup>
                        <FormControl
                            placeholder="Escribe un mensaje..."
                            aria-label="Escribe un mensaje"
                            value={message}
                            onChange={handleChangeMessage}
                            onBlur={handleBlurMessage}
                            disabled={!isConnectedRef.current}
                            style={{ borderRadius: '20px', padding: '10px' }}
                        />
                        <Button
                            variant="primary"
                            onClick={handleSendMessage}
                            disabled={!isConnectedRef.current || message.trim() === '' || sending}
                            style={{ borderRadius: '50%', height: '40px', width: '40px' }}
                        >
                            {sending ? <Spinner animation="border" size="sm" /> : <FaPaperPlane />}
                        </Button>
                    </InputGroup>
                    {!isConnectedRef.current && (
                        <Alert variant="danger" className="mt-2">
                            No se pudo conectar al servidor.
                        </Alert>
                    )}
                    {isConnectedRef.current && welcomeMessage && (
                        <Alert variant="success" className="mt-2 text-center">
                            {welcomeMessage}
                        </Alert>
                    )}
                </Card.Footer>
            </Card>
        </div>
    );
};

export default Mytrip;
